import { useState, useEffect, useRef } from 'react'
import { getRecentDonations, listenForRecurringDonationChanges } from '@services/DonationsService'
import { Org } from '@Interfaces/Data/OrganizationInterface'
import { Donation, RecurringDonation } from '@Interfaces/Data/DonationInterface'
import { UseDonationsDataReturn } from '@Interfaces/Hooks/DataInterface'

export const useDonationsData = (selectedOrg: Org | null): UseDonationsDataReturn => {
  const [recentDonations, setRecentDonations] = useState<Donation[]>([])
  const [, setGetDonations] = useState(false)
  const [ourDonations, setOurDonations] = useState<Donation[]>([])
  const [ourRecurringDonations, setOurRecurringDonations] = useState<RecurringDonation[]>([])
  const [selectedDonation, setSelectedDonation] = useState<Donation | null>(null)
  const lastOrgIdRef = useRef<string | null>(null)
  const subscriptionRef = useRef<(() => void) | null>(null)
  const processingRef = useRef<boolean>(false)

  useEffect(() => {
    if (!selectedOrg) {
      lastOrgIdRef.current = null
      processingRef.current = false
      if (subscriptionRef.current) {
        subscriptionRef.current()
        subscriptionRef.current = null
      }
      return
    }
    const orgKey = selectedOrg?.id ?? null
    // Prevent duplicate calls for the same org ID or if already processing
    if (lastOrgIdRef.current === orgKey || processingRef.current) return

    // Set flags immediately to prevent duplicate calls
    lastOrgIdRef.current = orgKey
    processingRef.current = true

    // Clean up previous subscription
    if (subscriptionRef.current) {
      subscriptionRef.current()
    }

    getRecentDonations(selectedOrg).then((response) => {
      if (response.success && response.data) {
        setRecentDonations(response.data)
      }
    })
    const sub = listenForRecurringDonationChanges(selectedOrg, setOurRecurringDonations, (error) =>
      console.error('Error loading recurring donations:', error)
    )
    subscriptionRef.current = sub
    processingRef.current = false

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current()
        subscriptionRef.current = null
      }
      processingRef.current = false
    }
  }, [selectedOrg?.id])

  return {
    recentDonations,
    setGetDonations,
    ourDonations,
    setOurDonations,
    ourRecurringDonations,
    selectedDonation,
    setSelectedDonation,
  }
}
